import { AppointmentType } from 'EntityTypes';
import { Omit } from './typesUtils';

export enum PatientSex {
  female = 'Female',
  male = 'Male',
  intersexAndOrOther = 'Other',
  unspecified = 'Unknown',
}

export type BasicPatientInfo = {
  id: number;
  email: string;
};

export type PatientInfo = {
  charts: number[];
} & BasicPatientInfo;

export type HydratedPatientInfo = {
  charts: PatientChartInfo[];
} & BasicPatientInfo;

export type PatientChartInfo = {
  belongsToPractice?: boolean;
  delegate?: boolean;
  id: number;
  firstName: string;
  lastName: string;
  actualName: string;
  dob: string;
  email?: string;
  phone?: string;
  sex: PatientSex;
  timezone?: string;
};

export type PatientChartRaw = Omit<
  PatientChartInfo,
  'belongsToPractice' | 'firstName' | 'lastName'
> & {
  belong_to_practice?: boolean;
  first_name: string;
  last_name: string;
};

export type PatientRaw = {
  charts: PatientChartRaw[];
} & BasicPatientInfo;

export type PatientLoginInfo = {
  password: string;
  email: string;
};

export type AddressInfo = {
  address1: string;
  address2?: string;
  city: string;
  state: string;
  zip: string;
  lat?: number;
  lng?: number;
};

export type PhysicianInfo = {
  id: number;
  firstName: string;
  lastName: string;
  credentials: string;
};

export type ServiceLocationInfo = {
  id: number;
  name: string;
  phone: string;
  address: string;
  fullAddress: string;
  city: string;
  lat?: number;
  lng?: number;
  state: string;
  suite: string;
  timezone?: string;
  zip: string;
  isPrimary: boolean;
};

// This is a temp solution for beta purposes only.
// The Availabilities API will be refactored to include
// serviceLocationId, making this temp type unnecessary
type PhysicianAppointmentType = {
  appointmentTypeId: number;
  physicianIds: number[];
};

// This is a temp solution for beta purposes only.
// The Availabilities API will be refactored to include
// serviceLocationId, making this temp type unnecessary
export type ServiceLocationAvailabilityInfo = {
  serviceLocationId: number;
  physicianAppointmentTypes: PhysicianAppointmentType[];
};

type SettingsInfo = {
  cancellationPolicy: string;
  contactEmail: string;
  practiceAddress: string;
  practiceInfo: string;
  phone: string;
  slug: string;
  patientBookingEnabled: boolean;
  showPracticeAddress: boolean;
  showLocationMap: boolean;
};

type BasicPracticeInfo = {
  id: number;
  name: string;
  phone: string;
  timezone: string;
  practiceInfo: string;
  lat?: number;
  lng?: number;
  settings: SettingsInfo;
  features: { patientCanCancel: boolean };
  // The below fields currently do exist on Practice entities fetched from the
  // server, but we don't want to use them so they're commented out. Use the fields
  // in BasicPracticeInfo.settings instead, as those are the fields the practice
  // has explicitly chosen to use & display for the booking site.

  // addressLine1: string;
  // addressLine2?: string;
  // city: string;
  // state: string;
  // zip: string;
  // phone?: string;
  // contactEmail?: string;
};

export type PracticeInfo = {
  appointmentTypes?: number[];
  physicians?: number[];
} & BasicPracticeInfo;

export type HydratedPracticeInfo = {
  appointmentTypes?: AppointmentType[];
  physicians?: PhysicianInfo[];
} & BasicPracticeInfo;

export type ClaimPatientInvite = {
  patientName: string;
  practiceName: string;
};

export type ClaimPatientFormValues = {
  dob: string;
  delegate: boolean;
};

export type ScheduleDayInfo = {
  startTime: string;
  endTime: string;
};

export type ScheduleInfo = {
  id: number;
  practiceId: number;
  monday?: ScheduleDayInfo;
  tuesday?: ScheduleDayInfo;
  wednesday?: ScheduleDayInfo;
  thursday?: ScheduleDayInfo;
  friday?: ScheduleDayInfo;
  saturday?: ScheduleDayInfo;
  sunday?: ScheduleDayInfo;
  [key: string]: number | ScheduleDayInfo;
};
